// The proposal-list nav section (the `navSections` seam). A compact, rail-sized
// changeset list — reuses the proposals data layer (`listChangesets`) + the
// shared row atoms (`StateBadge`, relative dates); the full-page `ProposalList`
// keeps its own `_/proposals` route. A row opens the review as a custom tab
// through `ctx.openTab` — no router coupling, the seam does the navigation.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { WorkbenchHostContext } from "@archlang/app-shell/board";
import { SkeletonRows } from "../../../ui/Skeleton";
import { Banner } from "../../../ui/Banner";
import { formatRelativeDate } from "../../../details/relativeDate";
import { cx } from "../../../lib/classNames";
import { StateBadge } from "../../../proposals/StateBadge";
import { useMe } from "../../../api/hooks/useMe";
import {
  listChangesets,
  type Changeset,
  type ChangesetState,
} from "../../../proposals/proposalsApi";
import { openReviewTab } from "./openReviewTab";
import { useTranslation } from "../../../i18n";

const TABS: { state: ChangesetState; labelKey: string }[] = [
  { state: "proposed", labelKey: "list.tabOpen" },
  { state: "draft", labelKey: "list.tabDrafts" },
  { state: "accepted", labelKey: "list.tabAccepted" },
  { state: "archived", labelKey: "list.tabArchived" },
];

const EMPTY_KEY: Record<ChangesetState, string> = {
  proposed: "list.emptyProposed",
  draft: "list.emptyDraft",
  accepted: "list.emptyAccepted",
  archived: "list.emptyArchived",
};

export function ProposalsNavPanel({ ctx }: { readonly ctx: WorkbenchHostContext }) {
  const { t } = useTranslation("proposals");
  const repoRef = ctx.activeRepo;
  const [tab, setTab] = useState<ChangesetState>("proposed");
  const meId = useMe().data?.id ?? null;
  const isMine = (cs: Changeset) => meId != null && cs.authorUserId === meId;

  const slug = repoRef?.slug ?? "";
  const repo = repoRef?.repo ?? "";
  const query = useQuery({
    queryKey: ["changesets", slug, repo, tab],
    queryFn: () => listChangesets(slug, repo, { state: tab }),
    enabled: repoRef != null,
  });

  if (repoRef == null) {
    return (
      <div className="p-3 text-xs text-slate-500">{t("list.noRepo", "No repository.")}</div>
    );
  }

  return (
    <div className="flex min-h-0 flex-col">
      <div className="flex flex-wrap gap-1 border-b border-slate-800 px-2 py-1.5">
        {TABS.map((tabDef) => (
          <button
            key={tabDef.state}
            type="button"
            onClick={() => setTab(tabDef.state)}
            aria-pressed={tab === tabDef.state}
            className={cx(
              "rounded px-1.5 py-0.5 text-xs transition-colors",
              tab === tabDef.state
                ? "bg-slate-800 font-medium text-slate-100"
                : "text-slate-400 hover:text-slate-200",
            )}
          >
            {t(tabDef.labelKey)}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {query.isLoading ? (
          <div className="p-2">
            <SkeletonRows rows={4} />
          </div>
        ) : query.error ? (
          <div className="p-2">
            <Banner kind="error">{String(query.error)}</Banner>
          </div>
        ) : query.data && query.data.length === 0 ? (
          <p className="p-4 text-center text-xs text-slate-500">{t(EMPTY_KEY[tab])}</p>
        ) : (
          <ul className="divide-y divide-slate-800">
            {query.data?.map((cs) => (
              <li key={cs.id}>
                <button
                  type="button"
                  onClick={() => openReviewTab((spec) => ctx.openTab(spec), slug, repo, cs)}
                  className="flex w-full items-start gap-2 px-2.5 py-2 text-left hover:bg-slate-800/40"
                >
                  <StateBadge state={cs.state} mine={isMine(cs)} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-slate-100">
                      {cs.title || t("list.untitled")}
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] text-slate-500">
                      {t("list.updated", { when: formatRelativeDate(cs.updatedAt) })}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
